import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { registerBeachAdmin } from '../services/firebase';
import { Input, Button, Spinner } from '../components/ui';

const STEPS = ['Account', 'Il tuo lido', 'Conferma'];

export default function Register() {
  const [step, setStep]         = useState(0);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: '', password: '', confirmPassword: '',
    nomeLido: '', indirizzo: '', telefono: '',
  });

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const validateStep0 = () => {
    if (!form.email || !form.password) return 'Compila tutti i campi.';
    if (form.password.length < 6) return 'La password deve avere almeno 6 caratteri.';
    if (form.password !== form.confirmPassword) return 'Le password non coincidono.';
    return null;
  };

  const validateStep1 = () => {
    if (!form.nomeLido || !form.indirizzo) return 'Inserisci nome e indirizzo del lido.';
    return null;
  };

  const next = () => {
    setError('');
    const err = step === 0 ? validateStep0() : step === 1 ? validateStep1() : null;
    if (err) { setError(err); return; }
    setStep((s) => s + 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      await registerBeachAdmin({
        email: form.email,
        password: form.password,
        nomeLido: form.nomeLido,
        indirizzo: form.indirizzo,
        telefono: form.telefono,
      });
      navigate('/dashboard');
    } catch (err) {
      setError(mapFirebaseError(err.code));
      setStep(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--navy)',
      backgroundImage: 'radial-gradient(ellipse at 30% 70%, rgba(42,110,127,0.1) 0%, transparent 60%)',
    }}>
      <div style={{ width: '100%', maxWidth: '440px', padding: '0 20px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <LogoSvg />
          <div style={{ fontFamily: 'var(--font-serif)', fontSize: '22px', marginTop: '10px' }}>
            Registra il tuo lido
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
            Crea il tuo account su Just Salento
          </div>
        </div>

        {/* Step indicator */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0', marginBottom: '24px' }}>
          {STEPS.map((label, i) => (
            <React.Fragment key={i}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  background: i <= step ? 'var(--ocean)' : 'var(--surface)',
                  border: `1px solid ${i <= step ? 'var(--ocean)' : 'var(--border-mid)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '11px', fontWeight: 500,
                  color: i <= step ? '#fff' : 'var(--text-muted)',
                  transition: 'all 0.2s',
                }}>
                  {i < step ? '✓' : i + 1}
                </div>
                <div style={{ fontSize: '10px', color: i === step ? 'var(--text-soft)' : 'var(--text-muted)' }}>
                  {label}
                </div>
              </div>
              {i < STEPS.length - 1 && (
                <div style={{
                  width: '48px', height: '1px', marginBottom: '16px', marginLeft: '2px', marginRight: '2px',
                  background: i < step ? 'var(--ocean)' : 'var(--border-mid)',
                  transition: 'background 0.2s',
                }} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Card */}
        <div style={{
          background: 'var(--navy-mid)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', padding: '28px',
        }}>
          {/* Step 0: Account */}
          {step === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <Input label="Email" type="email" placeholder="info@tuolido.it"
                value={form.email} onChange={set('email')} autoComplete="email" />
              <Input label="Password" type="password" placeholder="Minimo 6 caratteri"
                value={form.password} onChange={set('password')} autoComplete="new-password" />
              <Input label="Conferma password" type="password" placeholder="Ripeti la password"
                value={form.confirmPassword} onChange={set('confirmPassword')} />
            </div>
          )}

          {/* Step 1: Beach info */}
          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <Input label="Nome dello stabilimento" placeholder="es. Lido Le Dune"
                value={form.nomeLido} onChange={set('nomeLido')} />
              <Input label="Indirizzo" placeholder="Via delle Dune, 15, Avola (SR)"
                value={form.indirizzo} onChange={set('indirizzo')} />
              <Input label="Telefono (opzionale)" type="tel" placeholder="+39 0931 112233"
                value={form.telefono} onChange={set('telefono')} />
            </div>
          )}

          {/* Step 2: Summary */}
          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ fontSize: '14px', color: 'var(--text-soft)', marginBottom: '4px' }}>
                Controlla i dati prima di confermare:
              </div>
              {[
                ['Email', form.email],
                ['Lido', form.nomeLido],
                ['Indirizzo', form.indirizzo],
                form.telefono && ['Telefono', form.telefono],
              ].filter(Boolean).map(([label, value]) => (
                <div key={label} style={{
                  display: 'flex', justifyContent: 'space-between',
                  padding: '8px 0', borderBottom: '1px solid var(--border)',
                  fontSize: '13px',
                }}>
                  <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                  <span style={{ color: 'var(--text-soft)' }}>{value}</span>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div style={{
              background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.3)',
              borderRadius: 'var(--radius-md)', padding: '10px 12px',
              fontSize: '12.5px', color: '#f87171', marginTop: '14px',
            }}>
              {error}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            {step > 0 && (
              <Button variant="ghost" onClick={() => setStep(s => s - 1)} fullWidth>
                ← Indietro
              </Button>
            )}
            {step < 2 ? (
              <Button variant="primary" onClick={next} fullWidth>
                Avanti →
              </Button>
            ) : (
              <Button variant="sand" onClick={handleSubmit} disabled={loading} fullWidth>
                {loading ? <Spinner size={16} /> : 'Crea account'}
              </Button>
            )}
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: '16px', fontSize: '13px', color: 'var(--text-muted)' }}>
          Hai già un account?{' '}
          <Link to="/login" style={{ color: 'var(--ocean)', textDecoration: 'none' }}>
            Accedi
          </Link>
        </div>
      </div>
    </div>
  );
}

function LogoSvg() {
  return (
    <svg width="44" height="44" viewBox="0 0 36 36" fill="none" style={{ margin: '0 auto', display: 'block' }}>
      <circle cx="18" cy="18" r="18" fill="#2A6E7F" />
      <ellipse cx="18" cy="26" rx="10" ry="4" fill="#F5C842" opacity="0.9" />
      <path d="M12 22 Q18 14 24 22" stroke="#1f93e0" strokeWidth="1.5" fill="rgba(31,147,224,0.3)" />
      <line x1="18" y1="22" x2="18" y2="28" stroke="#c4a060" strokeWidth="1.5" />
      <path d="M12 17 Q18 11 24 17" stroke="white" strokeWidth="1.2" fill="rgba(255,255,255,0.25)" />
      <circle cx="24" cy="12" r="4" fill="#F5C842" opacity="0.9" />
    </svg>
  );
}

function mapFirebaseError(code) {
  const map = {
    'auth/email-already-in-use': 'Questa email è già registrata.',
    'auth/invalid-email':        'Indirizzo email non valido.',
    'auth/weak-password':        'Password troppo debole.',
  };
  return map[code] || 'Errore durante la registrazione. Riprova.';
}
